/**
 * Stay date display SSOT — strict DD/MM/YYYY (Israeli day-first).
 * Prevents locale-loose parsing (e.g. 12/07/2026 → 07/12/2026).
 *
 * Keep aligned with HotelsRiver_BE-main_whist/services/order_date_display_ssot.py
 * Trace: [ORDER_DATE_DISPLAY_SSOT_TRACE]
 */

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/he";

dayjs.extend(customParseFormat);

export const ORDER_DATE_DISPLAY_SSOT_TRACE_TAG = "[ORDER_DATE_DISPLAY_SSOT_TRACE]";

const STRICT_FORMATS = [
  "DD/MM/YYYY",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY HH:mm:ss",
  "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD HH:mm:ss",
  dayjs.ISO_8601,
];

function stripDateString(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s || s === "-" || s === "—") return null;
  return s;
}

/**
 * Parse date for display — strict DD/MM first; never US-style loose parse on ambiguous strings.
 */
export function parseOrderStayDateStrict(value) {
  const s = stripDateString(value);
  if (!s) return null;
  const strict = dayjs(s, STRICT_FORMATS, true);
  if (strict.isValid()) return strict;
  return null;
}

function reservationHotel(order) {
  const ors =
    order?.order_response && typeof order.order_response === "object"
      ? order.order_response
      : {};
  const res =
    ors.reservation && typeof ors.reservation === "object" ? ors.reservation : {};
  let hotel = res.hotel;
  if (!hotel || typeof hotel !== "object") {
    const arr = res.hotels || res.accommodations;
    if (Array.isArray(arr) && arr[0]) {
      hotel = arr[0].hotel && typeof arr[0].hotel === "object" ? arr[0].hotel : arr[0];
    }
  }
  return hotel && typeof hotel === "object" ? hotel : null;
}

/**
 * Raw stay date candidates (same priority as BE SSOT).
 */
export function resolveOrderStayDateSources(order) {
  const product = String(order?.type || "").trim().toLowerCase();
  const hotel = product === "package" ? reservationHotel(order) : null;
  return {
    product_type: product || null,
    api_checkIn: stripDateString(order?.checkIn ?? order?.check_in),
    api_checkOut: stripDateString(order?.checkOut ?? order?.check_out),
    dep_date: stripDateString(order?.dep_date),
    ret_date: stripDateString(order?.ret_date),
    checkin: stripDateString(order?.checkin ?? order?.check_in),
    checkout: stripDateString(order?.checkout ?? order?.check_out),
    hotel_checkInDate: hotel
      ? stripDateString(
          hotel.checkInDate ?? hotel.check_in_date ?? hotel.checkin ?? hotel.checkIn
        )
      : null,
    hotel_checkOutDate: hotel
      ? stripDateString(
          hotel.checkOutDate ?? hotel.check_out_date ?? hotel.checkout ?? hotel.checkOut
        )
      : null,
  };
}

function pickFirstValidDate(candidates) {
  for (const [source, raw] of candidates) {
    const parsed = parseOrderStayDateStrict(raw);
    if (parsed) {
      return { source, raw, formatted: parsed.format("DD/MM/YYYY"), parsed };
    }
  }
  return { source: null, raw: null, formatted: null, parsed: null };
}

/**
 * Canonical stay check-in / check-out for Recent Orders and aligned dashboards.
 */
export function resolveRecentOrderStayDates(order) {
  const sources = resolveOrderStayDateSources(order);
  const product = sources.product_type || "";
  const candidatesIn = [];
  const candidatesOut = [];

  if (product === "package") {
    if (sources.hotel_checkInDate) {
      candidatesIn.push(["hotel_checkInDate", sources.hotel_checkInDate]);
    }
    if (sources.hotel_checkOutDate) {
      candidatesOut.push(["hotel_checkOutDate", sources.hotel_checkOutDate]);
    }
    if (sources.dep_date) candidatesIn.push(["dep_date", sources.dep_date]);
    if (sources.ret_date) candidatesOut.push(["ret_date", sources.ret_date]);
    if (sources.api_checkIn) candidatesIn.push(["api_checkIn", sources.api_checkIn]);
    if (sources.api_checkOut) candidatesOut.push(["api_checkOut", sources.api_checkOut]);
  } else {
    if (sources.checkin) candidatesIn.push(["checkin", sources.checkin]);
    if (sources.checkout) candidatesOut.push(["checkout", sources.checkout]);
    if (sources.api_checkIn) candidatesIn.push(["api_checkIn", sources.api_checkIn]);
    if (sources.api_checkOut) candidatesOut.push(["api_checkOut", sources.api_checkOut]);
    if (sources.dep_date) candidatesIn.push(["dep_date", sources.dep_date]);
    if (sources.ret_date) candidatesOut.push(["ret_date", sources.ret_date]);
  }

  const inPick = pickFirstValidDate(candidatesIn);
  const outPick = pickFirstValidDate(candidatesOut);
  let stayRangeInverted = false;
  if (inPick.parsed && outPick.parsed && inPick.parsed.isAfter(outPick.parsed)) {
    stayRangeInverted = true;
  }

  return {
    checkIn: inPick.formatted,
    checkOut: outPick.formatted,
    checkInSource: inPick.source,
    checkOutSource: outPick.source,
    sources,
    stayRangeInverted,
  };
}

export function formatOrderStayDateDisplay(value) {
  const parsed = parseOrderStayDateStrict(value);
  return parsed ? parsed.format("DD/MM/YYYY") : "—";
}

/**
 * Hotel order-card stay date: Hebrew weekday + DD/MM/YYYY.
 * Example: "ראשון 06/12/2026"
 */
export function formatOrderStayDateWithWeekday(value) {
  if (!value || typeof value !== "string") return "";
  const parsed = parseOrderStayDateStrict(value);
  if (!parsed) return value;
  return `${parsed.locale("he").format("dddd")} ${parsed.format("DD/MM/YYYY")}`;
}

/**
 * Hotel order-card stay range. Keeps the card arrow and spacing.
 * Example: "ראשון 06/12/2026  →  חמישי 10/12/2026"
 */
export function formatOrderStayRangeWithWeekday(checkIn, checkOut) {
  return `${formatOrderStayDateWithWeekday(checkIn)}  →  ${formatOrderStayDateWithWeekday(checkOut)}`;
}

/**
 * Format stay range for Recent Orders widgets (card + table).
 */
export function formatRecentOrderStayDates(order) {
  const resolved = resolveRecentOrderStayDates(order);
  return {
    checkIn: resolved.checkIn ? formatOrderStayDateDisplay(resolved.checkIn) : "—",
    checkOut: resolved.checkOut ? formatOrderStayDateDisplay(resolved.checkOut) : "—",
    resolved,
  };
}

/** Simulate legacy RecentOrdersCard parse (loose dayjs first) for trace diff. */
export function simulateLegacyLooseDisplayDate(value) {
  const s = stripDateString(value);
  if (!s) return null;
  let parsed = dayjs(s);
  if (!parsed.isValid()) {
    parsed = dayjs(s, "DD/MM/YYYY", true);
  }
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : null;
}

export function buildOrderDateDisplaySsotTrace(order) {
  const resolved = resolveRecentOrderStayDates(order);
  const apiIn = resolved.sources.api_checkIn;
  const apiOut = resolved.sources.api_checkOut;
  const legacyIn = apiIn ? simulateLegacyLooseDisplayDate(apiIn) : null;
  const legacyOut = apiOut ? simulateLegacyLooseDisplayDate(apiOut) : null;
  return {
    order_id: order?.id ?? null,
    booking_ref: order?.booking_ref ?? null,
    product_type: resolved.sources.product_type,
    displayCheckIn: resolved.checkIn,
    displayCheckOut: resolved.checkOut,
    checkInSource: resolved.checkInSource,
    checkOutSource: resolved.checkOutSource,
    apiCheckIn: apiIn,
    apiCheckOut: apiOut,
    feLooseParseWouldDiffer:
      (legacyIn && resolved.checkIn && legacyIn !== resolved.checkIn) ||
      (legacyOut && resolved.checkOut && legacyOut !== resolved.checkOut),
    legacyLooseCheckIn: legacyIn,
    legacyLooseCheckOut: legacyOut,
    stayRangeInverted: resolved.stayRangeInverted,
    raw: resolved.sources,
  };
}

export function traceOrderDateDisplaySsot(order) {
  const payload = buildOrderDateDisplaySsotTrace(order);
  if (import.meta.env?.DEV) {
    console.info(ORDER_DATE_DISPLAY_SSOT_TRACE_TAG, payload);
  }
  return payload;
}
